/* eslint-disable no-unused-vars */
import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";
import PropTypes from "prop-types";
import { PDFDownloadLink, PDFViewer, BlobProvider } from "@react-pdf/renderer";
import CustomerFormationsListTable from "./CustomerFormationsListTable";
import { sessionApi } from "../../../api/sessionApi";
import { customerApi } from "../../../api/customerApi";
import toast from "react-hot-toast";
import EmailSendDialog from "./EmailSendDialog";
import {
  Autocomplete,
  ToggleButton,
  Button,
  DialogActions,
  ToggleButtonGroup,
  Card,
  Grid,
  Box,
  CardContent,
  CardHeader,
  Link,
  InputAdornment,
  List,
  Dialog,
  ListItem,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Chip,
  LinearProgress,
  IconButton,
  Avatar,
  Tooltip,
  Paper,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Email as EmailIcon,
  List as ListIcon,
  Apps as AppsIcon,
  PictureAsPdf as PdfIcon,
  History as HistoryIcon,
  ChevronRight as ChevronRightIcon,
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Timer as TimerIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";
import { motion, AnimatePresence } from "framer-motion";
import AttestationPDF from "../../AttestationPDF";
import * as moment from "moment";
import Label from "../../Label";

const applyFilters = (formations, query) =>
  formations.filter((formation) => {
    let matches = true;

    if (query) {
      const properties = ["name", "sourceFirstname", "sourceLastname"];
      let containsQuery = false;

      properties.forEach((property) => {
        if (formation[property]?.toLowerCase().includes(query.toLowerCase())) {
          containsQuery = true;
        }
      });

      if (!containsQuery) {
        matches = false;
      }
    }
    return matches;
  });

const sortOptions = [
  { label: "Plus récentes", value: "start_date|desc" },
  { label: "Plus anciennes", value: "start_date|asc" },
  { label: "Plus avancé", value: "progression|desc" },
  { label: "Moins avancé", value: "progression|asc" },
];

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
};

const getComparator = (order, orderBy) =>
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);

const applySort = (formations, sort) => {
  const [orderBy, order] = sort.split("|");
  const comparator = getComparator(order, orderBy);
  const stabilizedThis = formations.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const newOrder = comparator(a[0], b[0]);
    if (newOrder !== 0) return newOrder;
    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
};

const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case "Inscrit":
        return { color: "#10b981", bgcolor: "rgba(16, 185, 129, 0.1)", label: "Inscrit" };
      case "En attente":
      case "À transférer":
        return { color: "#f59e0b", bgcolor: "rgba(245, 158, 11, 0.1)", label: status };
      case "InscritD":
        return { color: "#6366f1", bgcolor: "rgba(99, 102, 241, 0.1)", label: "Inscrit D" };
      case "Décalé":
        return { color: "#8b5cf6", bgcolor: "rgba(139, 92, 246, 0.1)", label: "Décalé" };
      default:
        return { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.1)", label: status };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        bgcolor: config.bgcolor,
        color: config.color,
        fontWeight: 800,
        fontSize: "0.65rem",
        borderRadius: "8px",
        border: `1px solid ${config.color}20`,
      }}
    />
  );
};

const FormationCard = ({ formation, apprenant, orientations, setViewPDF, setSelectedFormation }) => {
  const isCompleted = formation.progression === 100;

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      layout
      style={{ height: "100%" }}
    >
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          position: "relative",
          "&:hover": {
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }
        }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <StatusChip status={formation.status} />
          <Tooltip title="Voir la session">
            <IconButton 
              component={RouterLink} 
              to={`/dashboard/session/${formation.session_id}`}
              size="small"
              sx={{ bgcolor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", "&:hover": { bgcolor: "#f1f5f9" } }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <CardContent sx={{ pt: 0, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ minHeight: "4.5rem", mb: 1.5 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 900, 
                color: "#1e293b", 
                lineHeight: 1.2, 
                fontSize: "0.95rem"
              }}
            >
              {formation.name}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2, minHeight: "2.5rem" }}>
            <Box sx={{ p: 0.5, borderRadius: "6px", bgcolor: "#f1f5f9", color: "#64748b", display: "flex" }}>
              <HistoryIcon sx={{ fontSize: 14 }} />
            </Box>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
              Session #{formation.number_session} • N° du programme: {formation.programNumber}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", fontSize: "0.6rem" }}>Début</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "#334155" }}>
                  {moment(formation.start_date).format("DD/MM/YY")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", fontSize: "0.6rem" }}>Fin</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "#334155" }}>
                  {moment(formation.end_date).format("DD/MM/YY")}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>Progression</Typography>
              <Typography variant="caption" sx={{ color: formation.progression > 70 ? "#10b981" : "#6366f1", fontWeight: 900 }}>{formation.progression}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={formation.progression} 
              sx={{ 
                height: 6, 
                borderRadius: 3, 
                bgcolor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  background: formation.progression > 70 
                    ? "linear-gradient(90deg, #10b981 0%, #34d399 100%)" 
                    : "linear-gradient(90deg, #6366f1 0%, #818cf8 100%)"
                }
              }} 
            />
          </Box>

          <Divider sx={{ my: 2, opacity: 0.6 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar 
                sx={{ width: 24, height: 24, bgcolor: "rgba(99, 102, 241, 0.1)", color: "primary.main", fontSize: "0.65rem", fontWeight: 800 }}
              >
                {formation.sourceFirstname?.[0] || "S"}
              </Avatar>
              <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600 }}>
                {formation.sourceFirstname} {formation.sourceLastname}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 900, color: "#1e293b" }}>
              {formation.real_price} €
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
        </CardContent>

        <Box sx={{ p: 1, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", gap: 1 }}>
          <Button 
            fullWidth 
            size="small" 
            variant="contained"
            startIcon={<PdfIcon fontSize="small" />}
            onClick={() => {
              setSelectedFormation(formation);
              setViewPDF(true);
            }}
            sx={{ 
              borderRadius: "12px", 
              textTransform: "none", 
              fontWeight: 800, 
              bgcolor: "rgba(99, 102, 241, 0.1)",
              color: "primary.main",
              "&:hover": { bgcolor: "primary.main", color: "white" }
            }}
          >
            Attestation & PDF
          </Button>
        </Box>
      </Card>
    </motion.div>
  );
};

const CustomerFormationsList = (props) => {
  const {
    formations,
    setFormations,
    apprenant,
    orientations,
    formationsDec,
    sources,
    formationsInformations,
    user,
    ...other
  } = props;

  const [showStats, setShowStats] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(sortOptions[0].value);
  const [viewPDF, setViewPDF] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [idSession, setIdSession] = useState();
  const [indexFormation, setIndexFormation] = useState(0);
  const [valueIdSession, setValueIdSession] = useState(false);
  const [disableChoiceSessions, setDisableChoiceSessions] = useState(true);
  const [selectedFormation, setSelectedFormation] = useState(null);

  const filteredFormations = applyFilters(formations, query);
  const sortedFormations = applySort(filteredFormations, sort);

  const handleQueryChange = (event) => setQuery(event.target.value);
  const handleSortChange = (event) => setSort(event.target.value);
  const handleToggleView = () => setShowStats(!showStats);
  const handleOpenAddDialog = () => setShowAddDialog(true);
  const handleCloseAddDialog = () => setShowAddDialog(false);
  const handleOpenEmailDialog = () => setShowEmailDialog(true);
  const handleCloseEmailDialog = () => setShowEmailDialog(false);

  const formationsFiltrees = formations.filter(
    (f) =>
      (moment(f.start_date).year() === moment().year() ||
        moment(f.start_date).year() === moment().year() + 1) &&
      (f.status === "Inscrit" || f.status === "InscritD")
  );

  const handleAddFormation = async () => {
    try {
      await sessionApi.addCustomerFormation(
        apprenant.id,
        idSession,
        localStorage.getItem("accessToken")
      );
      toast.success("Apprenant ajouté à la formation !");
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'ajout à la formation.");
    }
  };

  return (
    <Box {...other}>
      {/* Search and Filters Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "24px",
          bgcolor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.07)",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Rechercher une formation..."
          value={query}
          onChange={handleQueryChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            minWidth: "250px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              bgcolor: "white",
              "& fieldset": { borderColor: "#e2e8f0" },
            },
          }}
        />

        <TextField
          select
          size="small"
          label="Trier par"
          value={sort}
          onChange={handleSortChange}
          SelectProps={{ native: true }}
          sx={{
            width: "200px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              bgcolor: "white",
              "& fieldset": { borderColor: "#e2e8f0" },
            },
          }}
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>

        <Box sx={{ display: "flex", gap: 1.5, ml: { md: "auto" } }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 800,
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              px: 3,
            }}
          >
            Nouvelle formation
          </Button>

          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handleOpenEmailDialog}
            disabled={![0, 4].includes(user.id_service)}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 800,
              borderWidth: "2px",
              "&:hover": { borderWidth: "2px" },
              px: 3,
            }}
          >
            Email
          </Button>

          <Tooltip title={showStats ? "Vue Cartes" : "Vue Tableau"}>
            <IconButton 
              onClick={handleToggleView}
              sx={{ 
                bgcolor: "white", 
                border: "2px solid #e2e8f0", 
                borderRadius: "14px",
                color: "#64748b"
              }}
            >
              {showStats ? <AppsIcon /> : <ListIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Content Area */}
      <AnimatePresence mode="wait">
        {!showStats ? (
          <motion.div
            key="grid-view"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={3} alignItems="stretch">
              {sortedFormations.map((formation) => (
                <Grid item key={formation.id} xs={12} sm={6} md={4} lg={3}>
                  <FormationCard 
                    formation={formation} 
                    apprenant={apprenant} 
                    orientations={orientations}
                    setViewPDF={setViewPDF}
                    setSelectedFormation={setSelectedFormation}
                  />
                </Grid>
              ))}
              {sortedFormations.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ py: 10, textAlign: "center" }}>
                    <Typography variant="h6" color="textSecondary">
                      Aucune formation trouvée
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </motion.div>
        ) : (
          <motion.div
            key="table-view"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3 }}
          >
            <CustomerFormationsListTable 
              formations={sortedFormations}
              setFormations={setFormations}
              formationsDec={formationsDec}
              sources={[
                { id: 997, firstname: "Pas", lastname: "connu" },
                ...sources
              ]}
              getCustomer={other.getCustomer}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Formation Dialog */}
      <Dialog 
        open={showAddDialog} 
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: "1.25rem", color: "#1e293b" }}>
          Inscrire à une formation
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <Autocomplete
              options={formationsDec}
              getOptionLabel={(option) => `${option.name} (${option.program_number})`}
              onChange={(e, value) => {
                if (value) {
                  setDisableChoiceSessions(false);
                  setIndexFormation(formationsDec.indexOf(value));
                } else {
                  setDisableChoiceSessions(true);
                  setIndexFormation(0);
                }
                setIdSession(null);
                setValueIdSession(!valueIdSession);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Formation" variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }} />
              )}
            />
            <Autocomplete
              key={valueIdSession}
              disabled={disableChoiceSessions || !formationsDec[indexFormation]?.sessions?.length}
              options={formationsDec[indexFormation]?.sessions || []}
              getOptionLabel={(option) => `${option.number_session} • ${moment(option.start_date).format("DD/MM/YYYY")} - ${moment(option.end_date).format("DD/MM/YYYY")}`}
              onChange={(e, value) => setIdSession(value?.id)}
              noOptionsText="Aucune session disponible"
              renderInput={(params) => (
                <TextField {...params} label="Session" variant="outlined" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }} />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddDialog} color="inherit" sx={{ fontWeight: 700 }}>Annuler</Button>
          <Button 
            onClick={handleAddFormation} 
            variant="contained" 
            disabled={!idSession}
            sx={{ borderRadius: "12px", px: 4, fontWeight: 800 }}
          >
            Confirmer l'inscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog 
        fullScreen 
        open={viewPDF} 
        onClose={() => setViewPDF(false)}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, bgcolor: "white", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => setViewPDF(false)}><ArrowBackIcon /></IconButton>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Aperçu de l'attestation</Typography>
          </Box>
          <Box sx={{ flexGrow: 1, bgcolor: "#525659", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {selectedFormation && (
              <BlobProvider
                document={
                  <AttestationPDF
                    customer={apprenant}
                    formation={selectedFormation}
                    orientations={orientations}
                  />
                }
              >
                {({ blob, url, loading, error }) => {
                  if (loading) {
                    return (
                      <Box sx={{ textAlign: "center", color: "white" }}>
                        <CircularProgress color="inherit" sx={{ mb: 2 }} />
                        <Typography>Génération du PDF...</Typography>
                      </Box>
                    );
                  }
                  if (error) {
                    return (
                      <Box sx={{ textAlign: "center", color: "white", p: 3 }}>
                        <Typography variant="h6">Erreur lors de la génération</Typography>
                        <Typography variant="body2">{error.message}</Typography>
                      </Box>
                    );
                  }
                  return (
                    <iframe
                      title="PDF Preview"
                      src={url}
                      style={{ width: "100%", height: "100%", border: "none" }}
                    />
                  );
                }}
              </BlobProvider>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Email Dialog */}
      <EmailSendDialog
        open={showEmailDialog}
        onClose={handleCloseEmailDialog}
        apprenant={apprenant}
        formations={formationsFiltrees}
      />
    </Box>
  );
};

CustomerFormationsList.propTypes = {
  formations: PropTypes.array.isRequired,
  setFormations: PropTypes.func.isRequired,
  apprenant: PropTypes.object.isRequired,
  orientations: PropTypes.array.isRequired,
  formationsDec: PropTypes.array.isRequired,
  sources: PropTypes.array.isRequired,
  formationsInformations: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  getCustomer: PropTypes.func.isRequired,
};

export default CustomerFormationsList;


